import type { JSONSchema7 } from 'json-schema';
import React from 'react';

import * as api from '@gitbook/api-client';
import { SymbolsTable } from '@gitbook/expr';
import { Description } from '@gitbook/spine-react';

import { ExpressionEditor, ExpressionProvider } from '../Expressions';

type MergeRuleCustom = Extract<api.MergeRule, { rule: 'custom' }>;

/**
 * Editor for the custom merge rule configuration.
 */
export function MergeRuleCustomEditor(props: {
    mergeRule: MergeRuleCustom;
    onChange: (mergeRule: MergeRuleCustom) => void;
}) {
    const { mergeRule, onChange } = props;

    const symbolsTable = React.useMemo(() => {
        const userSchema: JSONSchema7 = {
            type: 'object',
            description: 'The attributes tied to a user.',
            properties: {
                id: {
                    type: 'string',
                    description: 'The ID of the user.',
                },
            },
            required: ['id'],
        };

        return new SymbolsTable({
            changeRequest: SymbolsTable.inferSymbolFromJSONSchema(
                {
                    type: 'object',
                    description: 'The attributes tied to the current change request.',
                    properties: {
                        subject: {
                            type: 'string',
                            description: 'The subject of the change request.',
                        },
                        description: {
                            type: 'object',
                            description: 'The description of the change request.',
                        },
                        outdated: {
                            type: 'boolean',
                            description: 'Whether the change request is outdated.',
                        },
                        createdBy: userSchema,
                    },
                    required: ['subject', 'outdated', 'createdBy'],
                },
                'changeRequest'
            ),
            reviews: SymbolsTable.inferSymbolFromJSONSchema(
                {
                    type: 'array',
                    description: 'The reviews of the change request.',
                    items: {
                        type: 'object',
                        description: 'The attributes tied to the current review.',
                        properties: {
                            status: {
                                type: 'string',
                                description: 'The status of the review.',
                                enum: [
                                    api.ChangeRequestReviewStatus.Approved,
                                    api.ChangeRequestReviewStatus.ChangesRequested,
                                ],
                            },
                            reviewer: userSchema,
                        },
                    },
                },
                'reviews'
            ),
            mergedBy: SymbolsTable.inferSymbolFromJSONSchema(userSchema, 'mergedBy'),
        });
    }, []);

    return (
        <ExpressionProvider symbolsTable={symbolsTable}>
            <div className="flex flex-col gap-3">
                <Description>
                    The change request can be merged if the expression evaluates to true.
                </Description>
                <div className="rounded-lg border bg-base">
                    <ExpressionEditor
                        value={mergeRule.expression}
                        onChange={(expression) => {
                            onChange({ ...mergeRule, expression });
                        }}
                        autoFocus
                        size="large"
                        documentationURL={{
                            path: 'merge-rules', // TODO
                        }}
                    />
                </div>
            </div>
        </ExpressionProvider>
    );
}
